/**
 * Thread Cache Manager
 * Implements LRU cache with size limits and cache invalidation
 * Requirements: 8.5
 */

class ThreadCacheManager {
  constructor(options = {}) {
    this.maxSize = options.maxSize || 100;
    this.threadTTL = options.threadTTL || 300000; // 5 minutes
    this.messageTTL = options.messageTTL || 600000; // 10 minutes
    this.replyTTL = options.replyTTL || 300000; // 5 minutes

    // Cache stores
    this.threadCache = new Map();
    this.messageCache = new Map();
    this.replyCache = new Map();
    this.accessTimes = new Map();

    // Cache statistics
    this.stats = {
      hits: 0,
      misses: 0,
      evictions: 0,
      invalidations: 0,
    };

    // Start cleanup interval
    this.startCleanupInterval();
  }

  /**
   * Cache thread data
   * @param {string} threadId - Thread ID
   * @param {Object} threadData - Thread data to cache
   */
  cacheThread(threadId, threadData) {
    if (!threadId || !threadData) return;

    const cacheEntry = {
      data: threadData,
      timestamp: Date.now(),
      accessCount: 1,
      type: "thread",
    };

    this.threadCache.set(threadId, cacheEntry);
    this.accessTimes.set(threadId, Date.now());

    // Enforce size limit
    this.enforceSize();

    if (process.env.NODE_ENV === "development") {
      console.log(`🗄️ Thread cached: ${threadId}`);
    }
  }

  /**
   * Get cached thread data
   * @param {string} threadId - Thread ID
   * @returns {Object|null} Cached thread data or null
   */
  getCachedThread(threadId) {
    if (!threadId) return null;

    const entry = this.threadCache.get(threadId);
    if (!entry) {
      this.stats.misses++;
      return null;
    }

    // Check if expired
    if (this.isExpired(entry, this.threadTTL)) {
      this.threadCache.delete(threadId);
      this.accessTimes.delete(threadId);
      this.stats.misses++;
      return null;
    }

    // Update access info
    entry.accessCount++;
    this.accessTimes.set(threadId, Date.now());
    this.stats.hits++;

    if (process.env.NODE_ENV === "development") {
      console.log(`✅ Thread cache hit: ${threadId}`);
    }

    return entry.data;
  }

  /**
   * Cache messages for a thread
   * @param {string} threadId - Thread ID
   * @param {Array} messages - Messages to cache
   * @param {Object} pagination - Pagination info
   */
  cacheMessages(threadId, messages, pagination = {}) {
    if (!threadId || !Array.isArray(messages)) return;

    const cacheKey = `${threadId}_messages`;
    const cacheEntry = {
      data: {
        messages,
        pagination,
        threadId,
      },
      timestamp: Date.now(),
      accessCount: 1,
      type: "messages",
    };

    this.messageCache.set(cacheKey, cacheEntry);
    this.accessTimes.set(cacheKey, Date.now());

    // Also cache individual messages
    messages.forEach((message) => {
      if (message._id) {
        this.cacheMessage(message._id, message);
      }
    });

    this.enforceSize();

    if (process.env.NODE_ENV === "development") {
      console.log(
        `💬 Messages cached for thread: ${threadId} (${messages.length} messages)`
      );
    }
  }

  /**
   * Get cached messages for a thread
   * @param {string} threadId - Thread ID
   * @returns {Object|null} Cached messages data or null
   */
  getCachedMessages(threadId) {
    if (!threadId) return null;

    const cacheKey = `${threadId}_messages`;
    const entry = this.messageCache.get(cacheKey);

    if (!entry) {
      this.stats.misses++;
      return null;
    }

    // Check if expired
    if (this.isExpired(entry, this.messageTTL)) {
      this.messageCache.delete(cacheKey);
      this.accessTimes.delete(cacheKey);
      this.stats.misses++;
      return null;
    }

    // Update access info
    entry.accessCount++;
    this.accessTimes.set(cacheKey, Date.now());
    this.stats.hits++;

    return entry.data;
  }

  /**
   * Cache individual message
   * @param {string} messageId - Message ID
   * @param {Object} messageData - Message data
   */
  cacheMessage(messageId, messageData) {
    if (!messageId || !messageData) return;

    const cacheEntry = {
      data: messageData,
      timestamp: Date.now(),
      accessCount: 1,
      type: "message",
    };

    this.messageCache.set(messageId, cacheEntry);
    this.accessTimes.set(messageId, Date.now());
  }

  /**
   * Get cached message
   * @param {string} messageId - Message ID
   * @returns {Object|null} Cached message or null
   */
  getCachedMessage(messageId) {
    if (!messageId) return null;

    const entry = this.messageCache.get(messageId);
    if (!entry) return null;

    if (this.isExpired(entry, this.messageTTL)) {
      this.messageCache.delete(messageId);
      this.accessTimes.delete(messageId);
      return null;
    }

    entry.accessCount++;
    this.accessTimes.set(messageId, Date.now());
    return entry.data;
  }

  /**
   * Cache replies for a message
   * @param {string} messageId - Message ID
   * @param {Array} replies - Replies to cache
   * @param {Object} pagination - Pagination info
   */
  cacheReplies(messageId, replies, pagination = {}) {
    if (!messageId || !Array.isArray(replies)) return;

    const cacheKey = `${messageId}_replies`;
    const cacheEntry = {
      data: {
        replies,
        pagination,
        messageId,
      },
      timestamp: Date.now(),
      accessCount: 1,
      type: "replies",
    };

    this.replyCache.set(cacheKey, cacheEntry);
    this.accessTimes.set(cacheKey, Date.now());

    this.enforceSize();

    if (process.env.NODE_ENV === "development") {
      console.log(
        `💭 Replies cached for message: ${messageId} (${replies.length} replies)`
      );
    }
  }

  /**
   * Get cached replies for a message
   * @param {string} messageId - Message ID
   * @returns {Object|null} Cached replies data or null
   */
  getCachedReplies(messageId) {
    if (!messageId) return null;

    const cacheKey = `${messageId}_replies`;
    const entry = this.replyCache.get(cacheKey);

    if (!entry) {
      this.stats.misses++;
      return null;
    }

    if (this.isExpired(entry, this.replyTTL)) {
      this.replyCache.delete(cacheKey);
      this.accessTimes.delete(cacheKey);
      this.stats.misses++;
      return null;
    }

    entry.accessCount++;
    this.accessTimes.set(cacheKey, Date.now());
    this.stats.hits++;

    return entry.data;
  }

  /**
   * Invalidate thread cache
   * @param {string} threadId - Thread ID to invalidate
   */
  invalidateThread(threadId) {
    if (!threadId) return;

    // Remove thread cache
    this.threadCache.delete(threadId);
    this.accessTimes.delete(threadId);

    // Remove related message cache
    const messageCacheKey = `${threadId}_messages`;
    this.messageCache.delete(messageCacheKey);
    this.accessTimes.delete(messageCacheKey);

    this.stats.invalidations++;

    if (process.env.NODE_ENV === "development") {
      console.log(`🗑️ Thread cache invalidated: ${threadId}`);
    }
  }

  /**
   * Invalidate message cache
   * @param {string} messageId - Message ID to invalidate
   */
  invalidateMessage(messageId) {
    if (!messageId) return;

    // Remove message cache
    this.messageCache.delete(messageId);
    this.accessTimes.delete(messageId);

    // Remove related reply cache
    const replyCacheKey = `${messageId}_replies`;
    this.replyCache.delete(replyCacheKey);
    this.accessTimes.delete(replyCacheKey);

    this.stats.invalidations++;

    if (process.env.NODE_ENV === "development") {
      console.log(`🗑️ Message cache invalidated: ${messageId}`);
    }
  }

  /**
   * Update cached message (for likes, etc.)
   * @param {string} messageId - Message ID
   * @param {Object} updates - Updates to apply
   */
  updateCachedMessage(messageId, updates) {
    if (!messageId || !updates) return;

    const entry = this.messageCache.get(messageId);
    if (entry && !this.isExpired(entry, this.messageTTL)) {
      entry.data = { ...entry.data, ...updates };
      entry.timestamp = Date.now(); // Refresh timestamp
      this.accessTimes.set(messageId, Date.now());
    }

    // Also update in thread messages cache
    for (const [key, cacheEntry] of this.messageCache.entries()) {
      if (key.endsWith("_messages") && cacheEntry.data.messages) {
        const messageIndex = cacheEntry.data.messages.findIndex(
          (msg) => msg._id === messageId
        );
        if (messageIndex !== -1) {
          cacheEntry.data.messages[messageIndex] = {
            ...cacheEntry.data.messages[messageIndex],
            ...updates,
          };
          cacheEntry.timestamp = Date.now();
        }
      }
    }
  }

  /**
   * Add new message to thread cache
   * @param {string} threadId - Thread ID
   * @param {Object} message - New message to add
   */
  addMessageToCache(threadId, message) {
    if (!threadId || !message) return;

    const messageCacheKey = `${threadId}_messages`;
    const entry = this.messageCache.get(messageCacheKey);

    if (entry && !this.isExpired(entry, this.messageTTL)) {
      // Add to beginning of messages array (newest first)
      entry.data.messages.unshift(message);
      entry.timestamp = Date.now();
      this.accessTimes.set(messageCacheKey, Date.now());

      // Update pagination
      if (entry.data.pagination) {
        entry.data.pagination.total = (entry.data.pagination.total || 0) + 1;
      }
    }

    // Cache the individual message
    this.cacheMessage(message._id, message);
  }

  /**
   * Check if cache entry is expired
   * @param {Object} entry - Cache entry
   * @param {number} ttl - Time to live in milliseconds
   * @returns {boolean} True if expired
   */
  isExpired(entry, ttl) {
    return Date.now() - entry.timestamp > ttl;
  }

  /**
   * Enforce cache size limits using LRU eviction
   */
  enforceSize() {
    const totalSize =
      this.threadCache.size + this.messageCache.size + this.replyCache.size;

    if (totalSize <= this.maxSize) return;

    // Get all entries with access times
    const allEntries = [];

    // Add thread entries
    for (const [key, entry] of this.threadCache.entries()) {
      allEntries.push({
        key,
        cache: "thread",
        accessTime: this.accessTimes.get(key) || 0,
        accessCount: entry.accessCount || 0,
      });
    }

    // Add message entries
    for (const [key, entry] of this.messageCache.entries()) {
      allEntries.push({
        key,
        cache: "message",
        accessTime: this.accessTimes.get(key) || 0,
        accessCount: entry.accessCount || 0,
      });
    }

    // Add reply entries
    for (const [key, entry] of this.replyCache.entries()) {
      allEntries.push({
        key,
        cache: "reply",
        accessTime: this.accessTimes.get(key) || 0,
        accessCount: entry.accessCount || 0,
      });
    }

    // Sort by access time (oldest first) and access count (least used first)
    allEntries.sort((a, b) => {
      if (a.accessTime !== b.accessTime) {
        return a.accessTime - b.accessTime;
      }
      return a.accessCount - b.accessCount;
    });

    // Remove oldest/least used entries
    const toRemove = totalSize - this.maxSize + 10; // Remove extra to avoid frequent cleanup
    for (let i = 0; i < toRemove && i < allEntries.length; i++) {
      const entry = allEntries[i];

      switch (entry.cache) {
        case "thread":
          this.threadCache.delete(entry.key);
          break;
        case "message":
          this.messageCache.delete(entry.key);
          break;
        case "reply":
          this.replyCache.delete(entry.key);
          break;
        default:
          // Unknown cache type, skip
          break;
      }

      this.accessTimes.delete(entry.key);
      this.stats.evictions++;
    }

    if (process.env.NODE_ENV === "development") {
      console.log(`🧹 Cache cleanup: removed ${toRemove} entries`);
    }
  }

  /**
   * Start periodic cleanup interval
   */
  startCleanupInterval() {
    // Clean up expired entries every 2 minutes
    this.cleanupInterval = setInterval(() => {
      this.cleanupExpired();
    }, 2 * 60 * 1000);
  }

  /**
   * Clean up expired entries
   */
  cleanupExpired() {
    let cleaned = 0;

    // Clean expired threads
    for (const [key, entry] of this.threadCache.entries()) {
      if (this.isExpired(entry, this.threadTTL)) {
        this.threadCache.delete(key);
        this.accessTimes.delete(key);
        cleaned++;
      }
    }

    // Clean expired messages
    for (const [key, entry] of this.messageCache.entries()) {
      if (this.isExpired(entry, this.messageTTL)) {
        this.messageCache.delete(key);
        this.accessTimes.delete(key);
        cleaned++;
      }
    }

    // Clean expired replies
    for (const [key, entry] of this.replyCache.entries()) {
      if (this.isExpired(entry, this.replyTTL)) {
        this.replyCache.delete(key);
        this.accessTimes.delete(key);
        cleaned++;
      }
    }

    if (cleaned > 0 && process.env.NODE_ENV === "development") {
      console.log(`🧹 Expired cache cleanup: removed ${cleaned} entries`);
    }
  }

  /**
   * Clear all caches
   */
  clearAll() {
    this.threadCache.clear();
    this.messageCache.clear();
    this.replyCache.clear();
    this.accessTimes.clear();

    this.stats = {
      hits: 0,
      misses: 0,
      evictions: 0,
      invalidations: 0,
    };

    if (process.env.NODE_ENV === "development") {
      console.log("🗑️ All caches cleared");
    }
  }

  /**
   * Get cache statistics
   * @returns {Object} Cache statistics
   */
  getStats() {
    const hitRate =
      this.stats.hits + this.stats.misses > 0
        ? (
            (this.stats.hits / (this.stats.hits + this.stats.misses)) *
            100
          ).toFixed(2)
        : 0;

    return {
      ...this.stats,
      hitRate: `${hitRate}%`,
      totalSize:
        this.threadCache.size + this.messageCache.size + this.replyCache.size,
      threadCacheSize: this.threadCache.size,
      messageCacheSize: this.messageCache.size,
      replyCacheSize: this.replyCache.size,
      memoryUsage: this.getMemoryUsage(),
    };
  }

  /**
   * Get estimated memory usage
   * @returns {number} Estimated memory usage in bytes
   */
  getMemoryUsage() {
    let totalSize = 0;

    // Rough estimation of memory usage
    for (const entry of this.threadCache.values()) {
      totalSize += JSON.stringify(entry).length * 2; // Rough estimate
    }

    for (const entry of this.messageCache.values()) {
      totalSize += JSON.stringify(entry).length * 2;
    }

    for (const entry of this.replyCache.values()) {
      totalSize += JSON.stringify(entry).length * 2;
    }

    return totalSize;
  }

  /**
   * Destroy cache manager
   */
  destroy() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
    this.clearAll();
  }
}

// Create singleton instance
const cacheManager = new ThreadCacheManager({
  maxSize: 100,
  threadTTL: 5 * 60 * 1000, // 5 minutes
  messageTTL: 10 * 60 * 1000, // 10 minutes
  replyTTL: 5 * 60 * 1000, // 5 minutes
});

export default cacheManager;
